import React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva } from 'class-variance-authority';
import { cn } from './utils';

const buttonVariants = cva(
  "tw-inline-flex tw-items-center tw-justify-center tw-gap-2 tw-whitespace-nowrap tw-rounded-md tw-text-sm tw-font-medium tw-transition-all tw-duration-200 disabled:tw-pointer-events-none disabled:tw-opacity-50 [&_svg]:tw-pointer-events-none [&_svg:not([class*='size-'])]:tw-size-4 tw-shrink-0 [&_svg]:tw-shrink-0 tw-outline-none tw-focus-visible:tw-border-ring dark:tw-focus-visible:tw-border-dark-ring tw-focus-visible:tw-ring-ring/50 tw-focus-visible:tw-ring-[3px] aria-invalid:tw-ring-destructive/20 dark:aria-invalid:tw-dark-ring-destructive/40 aria-invalid:tw-border-destructive",
  {
    variants: {
      variant: {
        default:
          'tw-bg-primary dark:tw-bg-dark-primary tw-text-primary-foreground dark:tw-text-dark-primary-foreground hover:tw-bg-primary-dark dark:hover:tw-bg-dark-primary-dark tw-shadow-sm hover:tw-shadow-md active:tw-scale-[0.98]',
        destructive:
          'tw-bg-destructive dark:tw-bg-dark-destructive tw-text-destructive-foreground dark:tw-text-dark-destructive-foreground hover:tw-bg-destructive-light dark:hover:tw-bg-dark-destructive-light tw-shadow-sm hover:tw-shadow-md active:tw-scale-[0.98] tw-focus-visible:tw-ring-destructive/20 dark:tw-focus-visible:tw-dark-ring-destructive/40',
        outline:
          'tw-border-2 tw-border-solid tw-border-primary tw-bg-background tw-text-primary tw-font-semibold hover:tw-bg-primary hover:tw-text-primary-foreground tw-shadow-sm hover:tw-shadow active:tw-scale-[0.98]',
        defaultOutline:
          'tw-border tw-border-solid tw-border-gray-300 tw-bg-background hover:tw-bg-primary hover:tw-text-primary-foreground tw-shadow-sm hover:tw-shadow active:tw-scale-[0.98]',
        secondary:
          'tw-bg-secondary dark:tw-bg-dark-secondary tw-text-secondary-foreground dark:tw-text-dark-secondary-foreground hover:tw-bg-secondary-light dark:hover:tw-bg-dark-secondary-light tw-shadow-sm hover:tw-shadow-md active:tw-scale-[0.98]',
        ghost:
          'hover:tw-bg-accent dark:hover:tw-bg-dark-accent hover:tw-text-accent-foreground dark:hover:tw-text-dark-accent-foreground active:tw-bg-accent/80 dark:active:tw-bg-dark-accent/80',
        link: 'tw-text-primary dark:tw-text-dark-primary tw-underline-offset-4 hover:tw-underline hover:tw-text-primary-dark dark:hover:tw-text-dark-primary-dark',
        success:
          'tw-bg-success dark:tw-bg-dark-success tw-text-success-foreground dark:tw-text-dark-success-foreground hover:tw-bg-success-light dark:hover:tw-bg-dark-success-light tw-shadow-sm hover:tw-shadow-md active:tw-scale-[0.98]',
        warning:
          'tw-bg-warning tw-text-warning-foreground hover:tw-bg-warning-light tw-shadow-sm hover:tw-shadow-md active:tw-scale-[0.98]',
        info: 'tw-bg-info tw-text-info-foreground hover:tw-bg-info-light tw-shadow-sm hover:tw-shadow-md active:tw-scale-[0.98]',
        error:
          'tw-border-2 tw-border-solid tw-border-red-600 tw-bg-background tw-text-red-600 tw-font-semibold hover:tw-bg-red-600 hover:tw-text-white tw-shadow-sm hover:tw-shadow active:tw-scale-[0.98]',
      },
      size: {
        default: 'tw-h-9 tw-px-4 tw-py-2 has-[>svg]:tw-px-3',
        xs: 'tw-h-6 tw-rounded-md tw-gap-1 tw-px-2 has-[>svg]:tw-px-1.5',
        sm: 'tw-h-8 tw-rounded-md tw-gap-1.5 tw-px-3 has-[>svg]:tw-px-2.5',
        lg: 'tw-h-10 tw-rounded-md tw-px-6 has-[>svg]:tw-px-4',
        icon: 'tw-size-9 tw-rounded-md',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

const Button = React.forwardRef(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';

    return (
      <Comp
        data-slot='button'
        className={cn(buttonVariants({ variant, size }), className)}
        ref={ref}
        {...props}
      />
    );
  }
);

Button.displayName = 'Button';

export { Button, buttonVariants };
